import { Button } from '@/components/ui/button';
import { CountryDropdown } from '@/components/ui/country-dropdown';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { countries } from 'country-data-list';
import { getCountryCallingCode } from 'libphonenumber-js/max';
import { PhoneIcon } from 'lucide-react';
import { useId, useState } from 'react';
import type { TestCallIntent } from '../api/test-call.payload';
import { useConversationCalls } from '../api/use-conversation-calls';
import { useTestCall } from '../api/use-test-call';
import {
  handoffCountries,
  parseHandoffPhone,
  phoneCountry,
} from './handoff-phone';

const supportedCountries = countries.all.filter((entry) =>
  handoffCountries.some((code) => code === entry.alpha2)
);

export function TestCallForm({
  country: clinicCountry,
  available,
}: { country: string; available: boolean }) {
  const id = useId();
  const [recipient, setRecipient] = useState<TestCallIntent>(() => ({
    country: phoneCountry(clinicCountry),
    phoneNumber: '',
  }));
  const { country, phoneNumber: phone } = recipient;
  const [touched, setTouched] = useState(false);
  const test = useTestCall();
  const { calls } = useConversationCalls(
    test.data?.conversationId ?? '',
    Boolean(test.data)
  );
  const parsed = parseHandoffPhone(phone, country);
  const valid = parsed?.isValid() === true;
  const simulated = test.data?.callSid.startsWith('CAdryrun');
  const started = calls.find((call) => call.id === test.data?.callSid);
  const calling = Boolean(
    test.data && !simulated && (!started || started.status === 'in_progress')
  );
  const busy = test.isPending || calling;
  return (
    <div className="space-y-3 border-t pt-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">Test Claire by phone</p>
        <p className="text-sm text-muted-foreground">
          Get a call using your saved settings. Handoffs are disabled.
        </p>
      </div>
      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (valid && !busy && available) test.mutate(recipient);
        }}
      >
        <Label htmlFor={id}>Your phone number</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div
            className="flex h-9 min-w-0 flex-1 items-stretch rounded-md border border-input shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
            role="group"
            aria-label="Test phone number"
          >
            <CountryDropdown
              key={country}
              aria-label="Test country calling code"
              defaultValue={
                countries.all.find((entry) => entry.alpha2 === country)
                  ?.alpha3 ?? 'IRL'
              }
              options={supportedCountries}
              disabled={busy || !available}
              onChange={(entry) => {
                setRecipient((current) => ({
                  ...current,
                  country: phoneCountry(entry.alpha2),
                }));
                setTouched(false);
                test.reset();
              }}
              className="h-full w-auto rounded-r-none border-0 border-r px-2 shadow-none"
              slim
              align="start"
            />
            <PhoneInput
              id={id}
              type="tel"
              autoComplete="tel-national"
              value={phone}
              disabled={busy || !available}
              callingCode={`+${getCountryCallingCode(country)}`}
              placeholder="Enter number"
              aria-invalid={touched && !valid}
              aria-describedby={`${id}-help`}
              onChange={(value) => {
                const incoming = parseHandoffPhone(value, country);
                if (/^(\+|00)/.test(value.trim()) && incoming?.country) {
                  setRecipient({
                    country: phoneCountry(incoming.country),
                    phoneNumber: incoming.nationalNumber,
                  });
                } else
                  setRecipient((current) => ({
                    ...current,
                    phoneNumber: value,
                  }));
                test.reset();
              }}
              onBlur={() => setTouched(true)}
              className="h-full min-w-0 border-0 shadow-none focus-visible:ring-0"
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={!available || !valid || busy}
            className="h-9 shrink-0"
          >
            <PhoneIcon className="size-4" />
            {test.isPending
              ? 'Starting…'
              : calling
                ? 'Call in progress'
                : 'Call me'}
          </Button>
        </div>
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          UK, US and Ireland only. Uses the owner’s name and email. Bookings and
          messages are simulated. No texts or emails are sent.
        </p>
        {!available && (
          <p className="text-sm text-muted-foreground">
            Set up outbound calling to test Claire.
          </p>
        )}
        {touched && !valid && phone.trim() && (
          <p role="alert" className="text-sm text-destructive">
            Enter a valid UK, US or Irish phone number.
          </p>
        )}
        {test.error && (
          <p role="alert" className="text-sm text-destructive">
            {test.error.message || 'Could not start the call. Try again.'}
          </p>
        )}
        {test.data && (
          <p role="status" className="text-sm text-muted-foreground">
            {simulated
              ? 'Test call simulated. No phone was rung.'
              : calling
                ? 'Claire is calling. Answer your phone to try her.'
                : 'Test call finished. You can try again.'}
          </p>
        )}
      </form>
    </div>
  );
}
